import React, { useEffect, useState, useMemo } from "react";
import { feature } from "topojson-client";
import { geoPath, geoAlbersUsa } from "d3-geo";
import { CheckCircle2, MapPin } from "lucide-react";

const TOPO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

export const USMap = () => {
  const [geo, setGeo] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState({ x: 0, y: 0, visible: false });
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(TOPO_URL)
      .then((r) => {
        if (!r.ok) throw new Error("topojson fetch failed");
        return r.json();
      })
      .then((topo) => {
        if (cancelled) return;
        const states = feature(topo, topo.objects.states);
        setGeo(states);
      })
      .catch(() => !cancelled && setLoadError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const projection = useMemo(() => geoAlbersUsa().scale(1100).translate([480, 280]), []);
  const path = useMemo(() => geoPath(projection), [projection]);

  if (loadError) {
    return (
      <div
        data-testid="us-map-fallback"
        className="w-full h-[440px] rounded-2xl bg-brand-green-100 border border-[color:var(--brand-green-200)] flex items-center justify-center text-navy font-display text-2xl"
      >
        Licensing support available in all 50 states
      </div>
    );
  }

  if (!geo) {
    return (
      <div
        data-testid="us-map-loading"
        className="w-full h-[440px] rounded-2xl bg-[color:var(--brand-green-50)] animate-pulse"
      />
    );
  }

  return (
    <div className="relative" data-testid="us-map-container">
      <svg
        viewBox="0 0 960 560"
        className="w-full h-auto"
        role="img"
        aria-label="United States map with licensing support"
        onMouseLeave={() => {
          setHovered(null);
          setTooltip((t) => ({ ...t, visible: false }));
        }}
      >
        {geo.features.map((f) => (
          <path
            key={f.id}
            d={path(f)}
            className="us-map-state"
            data-testid={`us-state-${f.properties.name.replace(/\s+/g, "-").toLowerCase()}`}
            onMouseEnter={(e) => {
              setHovered(f.properties.name);
            }}
            onMouseMove={(e) => {
              const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
              setTooltip({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
                visible: true,
              });
            }}
          />
        ))}
      </svg>

      {tooltip.visible && hovered && (
        <div
          data-testid="us-map-tooltip"
          className="pointer-events-none absolute z-10 bg-navy text-white rounded-lg px-3 py-2 text-xs font-semibold shadow-lg"
          style={{
            left: tooltip.x + 12,
            top: tooltip.y + 12,
          }}
        >
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3 h-3" />
            {hovered}
          </div>
          <div className="text-[10px] font-normal opacity-80 mt-0.5">
            Licensing support available
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-3 text-sm text-slate-600 justify-center">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-4 rounded bg-[color:var(--brand-green-200)] border border-[color:var(--brand-green-300)]" />
          50-state coverage
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-4 rounded bg-navy" />
          Hover a state
        </span>
      </div>
    </div>
  );
};

const HIGHLIGHTS = [
  "Interstate Medical Licensure Compact (IMLC) fast-tracking",
  "DO, MD, NP, PA, BCBA & LCSW licenses across all 50 states",
  "FCVS & Uniform Application (UA) expertise",
  "Dedicated specialist for every application — no hand-offs",
  "Continuous board follow-ups to expedite approvals",
  "Transparent upfront pricing — no surprise fees",
  "Compliant and accurate applications the first time",
];

export const ProviderLicensing = () => (
  <section
    id="licensing"
    data-testid="licensing-section"
    className="py-20 md:py-28 bg-white"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8">
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-brand-green-100 text-[color:var(--brand-green-700)] px-4 py-1.5 text-xs font-bold tracking-[0.2em] uppercase">
          Provider Licensing
        </span>
        <h2 className="font-display mt-5 text-4xl md:text-5xl font-bold text-navy">
          We help your providers get licensed — in every state they need.
        </h2>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
          MediElite is not a licensing body. We are a licensing services partner that guides
          providers through multi-state applications, IMLC, FCVS, and Uniform Application — so your
          clinicians can start practicing sooner, anywhere in the US.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-12 gap-y-10 lg:gap-10 items-center">
        <div className="col-span-12 lg:col-span-7 bg-[color:var(--brand-green-50)] rounded-2xl p-6 md:p-10 border border-[color:var(--brand-green-200)]">
          <USMap />
        </div>

        <div className="col-span-12 lg:col-span-5">
          <h3 className="font-display text-2xl md:text-3xl text-navy">
            Hands-on with IMLC & multi-state licensing.
          </h3>
          <p className="mt-3 text-slate-600 leading-relaxed">
            State board timelines vary — we expedite approvals through continuous follow-ups,
            proven board relationships, and a zero-rework approach.
          </p>
          <ul className="mt-6 space-y-3">
            {HIGHLIGHTS.map((h, i) => (
              <li
                key={h}
                data-testid={`licensing-bullet-${i}`}
                className="flex items-start gap-3 text-[15px] text-slate-700"
              >
                <CheckCircle2 className="w-5 h-5 text-[color:var(--brand-green-700)] mt-0.5 shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);
